import dayjs from 'dayjs';
import i18n, { routeLocale, TRANSLATION_KEYS } from '@/i18n';
import { configureRelativeTime } from '@/plugins/dayjs/relativeTimeConfig';

export { TRANSLATION_KEYS };

// Languages the developer menu can show the texts in. Swedish names on
// purpose: the menu itself is Swedish (see CLAUDE.md).
export const UI_LANGUAGES = [
  { code: 'sv', name: 'Svenska' },
  { code: 'en', name: 'English' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'de', name: 'Deutsch' },
  { code: 'da', name: 'Dansk' }
];

const STORAGE_KEY = 'locale';
const LOCALES = [...UI_LANGUAGES.map(l => l.code), TRANSLATION_KEYS];

async function setDayjsLocale(lang) {
  if (lang !== 'en') {
    await import(`dayjs/locale/${lang}.js`);
  }
  dayjs.locale(lang);
  configureRelativeTime(dayjs, lang);
}

// Switches the language of the texts. Paths, URLs, currency and APIs keep
// following the site's country (every language carries the country's paths,
// see src/i18n/index.js). TRANSLATION_KEYS shows each text's key instead.
export function setUiLocale(locale, { persist = true } = {}) {
  if (!LOCALES.includes(locale)) return;

  const showKeys = locale === TRANSLATION_KEYS;
  const lang = showKeys ? routeLocale : locale;

  i18n.locale = locale;
  // With keys shown, a missing text must stay a key instead of falling back
  // to English, and the many misses must not flood the console.
  i18n.fallbackLocale = showKeys ? TRANSLATION_KEYS : 'en';
  i18n.silentTranslationWarn = showKeys;
  document.documentElement.setAttribute('lang', lang);
  setDayjsLocale(lang).catch(() => {});

  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Storage blocked: the language lasts for this page load.
    }
  }
}

// Called once at startup: a language chosen in the developer menu sticks
// while the menu is on, and is forgotten once it is not.
export function restoreUiLocale(devMenuEnabled) {
  let stored = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
    if (!devMenuEnabled) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
  } catch {
    return;
  }

  if (stored && LOCALES.includes(stored) && stored !== i18n.locale) {
    setUiLocale(stored, { persist: false });
  }
}
